import { TrashIcon } from "@heroicons/react/24/outline";
import { getIn } from "formik";

import { useEntities } from "../../../hooks/useEntities";
import { getInput } from "../../ui";
import {
  getSalesPeopleRowsError,
  getSplitTotalPercentage,
  splitRowsEqually,
} from "./salesPeopleSplitEditorUtils";

/**
 * Sales people rows (user plus split, adding up to 1.0) held in a parent
 * formik form under `name`. The parent decides when they are saved.
 *
 * @param {object} props.formik - the form holding the rows
 * @param {string} props.name - path of the rows in the form values
 * @param {string} props.title - section heading, e.g. "Selling people"
 * @param {string} props.description - optional explanation under the heading
 * @param {ReactNode} props.headerAction - optional element beside the heading
 * @param {boolean} props.disabled
 */
const SalesPeopleSplitFields = props => {
  const {
    formik,
    name = "salesPeople",
    title,
    description,
    headerAction,
    disabled = false,
  } = props || {};
  const { entities } = useEntities();

  const rows = getIn(formik.values, name) || [];
  const rowsError = getSalesPeopleRowsError(rows);
  const totalPercentage = getSplitTotalPercentage(rows);
  const takenSalesUserIds = rows.map(row => row?.salesUserId);

  const salesUserOptions = currentSalesUserId =>
    entities?.edges
      ?.filter(
        entity =>
          entity?.id === currentSalesUserId ||
          !takenSalesUserIds.includes(entity?.id),
      )
      .map(entity => ({ label: entity?.name, value: entity?.id })) || [];

  const setRows = nextRows => formik.setFieldValue(name, nextRows);

  const addRow = () =>
    setRows([...rows, { salesUserId: null, split: rows.length ? 0 : 1 }]);

  const removeRow = index =>
    setRows(rows.filter((_, rowIndex) => rowIndex !== index));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-4">
        <h5 className="font-bold">{title}</h5>
        {headerAction}
      </div>
      {description && <p className="text-xs text-gray-500">{description}</p>}
      {rows.map((row, index) => (
        <div
          key={index}
          className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-end gap-2"
        >
          {getInput({
            formik,
            label: index === 0 ? "Sales User" : undefined,
            name: `${name}.${index}.salesUserId`,
            type: "multi-select",
            options: salesUserOptions(row?.salesUserId),
            disabled,
          })}
          {getInput({
            formik,
            label: index === 0 ? "Split (0-1)" : undefined,
            name: `${name}.${index}.split`,
            type: "number",
            step: 0.00001,
            allowEmpty: true,
            disabled,
          })}
          {!disabled && (
            <button
              type="button"
              aria-label="Remove sales person"
              className="mb-2 text-gray-500 hover:text-red-600"
              onClick={() => removeRow(index)}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={addRow}
          className="w-full cursor-pointer rounded border border-dashed border-gray-300 py-2 text-center text-xs text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700"
        >
          {rows.length ? "+ Add another sales person" : "Add sales person"}
        </button>
      )}
      {rows.length > 0 && (
        <div className="flex items-center justify-between gap-4 text-xs text-gray-600">
          <span>Allocated {totalPercentage.toFixed(2)}%</span>
          {!disabled && rows.length > 1 && (
            <button
              type="button"
              className="font-bold underline hover:text-black"
              onClick={() => setRows(splitRowsEqually(rows))}
            >
              Split equally
            </button>
          )}
        </div>
      )}
      {rowsError && <p className="text-xs text-red-500">{rowsError}</p>}
    </div>
  );
};

export { SalesPeopleSplitFields };
